import { useLogisticOperatorsOptions } from '@/pages/Business/pages/Commerce/hooks';
import { useAssignDriverLogisticOperatorOtp, useDriver } from '../../hooks';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { useGetDriverProfilePictureQuery } from '../../services';
import { DriverStatus, DriverStatusLabels } from '../../enums';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPhoneForDisplay } from '@/utils';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  AutocompleteInput,
  Authorize,
  GlobalButton,
  NavigateLinks,
  ReadOnlyAddressWithMapField,
  TextInput,
  WhatsAppInputAdornment,
} from '@/components';
import {
  DriverDocuments,
  DriverProfilePicture,
  LogisticOperators,
} from '../../components';
import {
  childrenDriversPageBreadcrumbs,
  DEFAULT_STATUS_CHIP_CLASS,
  DRIVER_STATUS_CHIP_CLASSES,
} from '../../config';

const ViewDriver = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const navigate = useNavigate();

  const { driver, isLoading, error } = useDriver({
    driverId: driverId || '',
  });

  const { data: profilePictureData } = useGetDriverProfilePictureQuery(
    { id: driverId || '' },
    { skip: !driverId }
  );
  const profilePictureUrl = profilePictureData?.imageUrl ?? null;

  const breadcrumbs = useMemo(
    () => childrenDriversPageBreadcrumbs(navigate),
    [navigate]
  );

  const fullName = driver
    ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
    : '';

  const [operatorSearch, setOperatorSearch] = useState('');
  const [selectedOperator, setSelectedOperator] = useState<Option | null>(null);

  const { operatorOptions, isLoadingOperators } = useLogisticOperatorsOptions({
    search: operatorSearch,
    minLength: 3,
    unscoped: true,
    onlyActive: true,
  });

  const handleOperatorSelect = (option: Option | null) => {
    setSelectedOperator(option?.data ? option : null);
  };

  const selectedOperatorId =
    (selectedOperator?.data as { id?: string })?.id || '';
  const selectedOperatorData = selectedOperator?.data as {
    name?: string;
    businessName?: string;
    status?: string;
  } | null;

  const {
    handleAssignOperator,
    handleResendOtp,
    isLoading: isAssigning,
    isResending,
    otpSent,
  } = useAssignDriverLogisticOperatorOtp();

  if (!driverId) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Conductor' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          El ID del conductor es requerido
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Conductor' breadcrumbs={breadcrumbs} />
        <div className='text-center text-gray-500 py-8'>Cargando...</div>
      </section>
    );
  }

  if (error || !driver?.id) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Conductor' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          Error al cargar el conductor o conductor no encontrado
        </div>
      </section>
    );
  }

  const statusLabel =
    DriverStatusLabels[driver.status as DriverStatus] || driver.status || '';

  const birthDateDisplay =
    driver.birthDate && dayjs(driver.birthDate).isValid()
      ? dayjs(driver.birthDate).format('DD/MM/YYYY')
      : '';

  return (
    <section className='flex flex-col gap-6 w-full h-full'>
      <NavigateLinks
        actualPage={fullName || 'Conductor'}
        breadcrumbs={breadcrumbs}
      />
      <div className='flex flex-wrap justify-end gap-3'>
        <Authorize
          permission={BUSINESS_ROLE_GROUPS.adminOrSupervisor}
          fallback={null}
        >
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => navigate(`/business/drivers/${driverId}/edit`)}
          >
            Editar conductor
          </GlobalButton>
        </Authorize>
      </div>
      <div className='w-full flex flex-col lg:flex-row gap-6'>
        <div className='flex-1 min-w-0 flex flex-col gap-6'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
              <h2 className='text-xl font-bold text-gray-800'>
                Información Personal
              </h2>
              <span
                className={`px-3 py-1 rounded-md text-sm font-semibold ${
                  DRIVER_STATUS_CHIP_CLASSES[driver.status] ??
                  DEFAULT_STATUS_CHIP_CLASS
                }`}
              >
                {statusLabel}
              </span>
            </div>
            <div className='flex flex-col sm:flex-row gap-6 mb-6'>
              <DriverProfilePicture
                fullName={fullName}
                profilePictureUrl={profilePictureUrl}
              />
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 min-w-0'>
                <TextInput
                  name='driver-full-name'
                  placeholder='Nombre completo'
                  value={fullName}
                  onChange={() => {}}
                  disabled
                />
                <TextInput
                  name='driver-document-number'
                  placeholder='Número de documento'
                  value={driver.documentNumber || ''}
                  onChange={() => {}}
                  disabled
                />
                <TextInput
                  name='driver-birth-date'
                  placeholder='Fecha de nacimiento'
                  value={birthDateDisplay}
                  onChange={() => {}}
                  disabled
                />
                <TextInput
                  name='driver-email'
                  placeholder='Correo electrónico'
                  value={driver.email || ''}
                  onChange={() => {}}
                  disabled
                />
                <TextInput
                  name='driver-work-phone'
                  placeholder='Teléfono laboral'
                  value={formatPhoneForDisplay(driver.workPhoneNumber)}
                  onChange={() => {}}
                  disabled
                  endAdornment={
                    <WhatsAppInputAdornment
                      phone={driver.workPhoneNumber}
                      aria-label='Abrir WhatsApp (teléfono laboral)'
                    />
                  }
                />
                <TextInput
                  name='driver-personal-phone'
                  placeholder='Teléfono personal'
                  value={formatPhoneForDisplay(driver.personalPhoneNumber)}
                  onChange={() => {}}
                  disabled
                  endAdornment={
                    <WhatsAppInputAdornment
                      phone={driver.personalPhoneNumber}
                      aria-label='Abrir WhatsApp (teléfono personal)'
                    />
                  }
                />
                <ReadOnlyAddressWithMapField
                  address={driver.address}
                  fieldLabel='Dirección'
                  mapModalTitle='Dirección del conductor'
                />
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-sm p-6'>
            <DriverDocuments driverId={driverId} />
          </div>
        </div>

        <div className='w-full lg:max-w-md'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>
              Operador logístico
            </h2>
            <div className='mb-6 pb-6 border-b border-gray-100'>
              <span className='text-sm font-bold text-gray-500 uppercase tracking-wide'>
                Operadores asignados
              </span>
              <LogisticOperators logisticOperators={driver.logisticOperators} />
            </div>
            <Authorize
              permission={BUSINESS_ROLE_GROUPS.operations}
              fallback={null}
            >
              <div className='flex flex-col gap-4'>
                <span className='text-sm font-bold text-gray-500 uppercase tracking-wide'>
                  Agregar otro operador logístico
                </span>
                <AutocompleteInput
                  name='driver-logistic-operator-assign'
                  placeholder={
                    isLoadingOperators
                      ? 'Cargando...'
                      : 'Buscar operador logístico...'
                  }
                  value={selectedOperator}
                  onChange={handleOperatorSelect}
                  onInputChange={setOperatorSearch}
                  options={operatorOptions}
                  required={false}
                  loading={isLoadingOperators}
                />
                {operatorSearch.length > 0 && operatorSearch.length < 3 && (
                  <p className='text-xs text-gray-500'>
                    Escribe al menos 3 letras para buscar.
                  </p>
                )}
                {selectedOperator && selectedOperatorData && (
                  <div className='rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 space-y-1'>
                    <p className='text-sm'>
                      <strong>Operador:</strong>{' '}
                      {selectedOperatorData.name || '—'}
                    </p>
                    <p className='text-sm text-gray-600'>
                      Razón social: {selectedOperatorData.businessName || '—'}
                    </p>
                  </div>
                )}
                {otpSent && (
                  <div className='flex flex-col gap-2 text-sm text-gray-600'>
                    <span>
                      El código para completar la asignación ha sido enviado al
                      conductor.
                    </span>
                    <GlobalButton
                      variant={GlobalButtonVariant.textButton}
                      action={() =>
                        handleResendOtp(driverId || '', selectedOperatorId)
                      }
                      disabled={isResending || isAssigning}
                    >
                      {isResending ? 'Reenviando...' : 'Reenviar código'}
                    </GlobalButton>
                  </div>
                )}
                <GlobalButton
                  variant={GlobalButtonVariant.primaryButton}
                  action={() =>
                    handleAssignOperator(driverId || '', selectedOperatorId)
                  }
                  disabled={!selectedOperatorId || isAssigning}
                >
                  {isAssigning ? 'Asignando...' : 'Asignar operador'}
                </GlobalButton>
              </div>
            </Authorize>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ViewDriver;
